import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import {
    ButtonsOptions,
    ColorOptions,
    FormSizeOptions,
    HorizontalAlignmentOptions,
    HtmlTagOptions,
    LayoutWidthOptions,
    TextStyleOptions,
    UndefinedOption
} from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICMS_CLIENT_CONFIG, NICmsClientConfig } from '@icms/client/config/icms-client.config';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import range from 'lodash-es/range';
import { TagComponent } from '../../ui/display/tag/tag.component';
export namespace NCmsText {
    export interface IComponentData extends NICmsContentComponent.IData {
        type: 'text' | 'button';
        align?: string;
        text?: string;
        cta_link?: string;
        // button
        button_class?: string;
        form_size?: string;
        // text
        text_style?: string;
        color?: string;
        tag?: string;
        column_align?: string;
        section_width?: string;
        content_width?: number;
        cta_label?: string;
    }
}
@ICmsComponent<CmsTextComponent>({
    id: 'text',
    name: 'Text/Button',
    image: '/common/cms/text-component.png',
    isAdvanced: true,
    tags: ['Homepage', 'Blog', 'Content', 'Text', 'Button'],
    fields: [
        // general
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        <NICmsContentField.IChoiceField>{
            key: 'content_width',
            type: NICmsContentField.EFieldType.choice,
            title: 'Content Width',
            itemName: 'content_width',
            options: range(1, 13).map(i => ({ key: i, title: i + '' })),
            default: 12,
            canShow: (data: NCmsText.IComponentData) => data.type !== 'button'
        },
        <NICmsContentField.IChoiceField>{
            key: 'type',
            type: NICmsContentField.EFieldType.choice,
            title: 'Type',
            options: [
                { key: 'text', title: 'Text Line' },
                { key: 'button', title: 'Button' }
            ],
            default: 'text'
        },
        <NICmsContentField.IChoiceField>{
            key: 'button_class',
            type: NICmsContentField.EFieldType.choice,
            title: 'Button Style',
            options: ButtonsOptions,
            default: ButtonsOptions[0] ? ButtonsOptions[0].key : undefined,
            canShow: (data: NCmsText.IComponentData) => data.type === 'button'
        },
        {
            key: 'text',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Copy',
            description: 'Text to display. HTML code can be used.',
            default: 'Lorem ipsum dolor',
            required: true,
            canShow: (data: NCmsText.IComponentData) => data?.type !== 'button'
        },
        {
            key: 'cta_label',
            type: NICmsContentField.EFieldType.text,
            title: 'Button Label',
            canShow: (data: NCmsText.IComponentData) => data?.type === 'button'
        },
        <NICmsContentField.IField>{
            key: 'cta_link',
            type: NICmsContentField.EFieldType.text,
            title: 'Button Link',
            canShow: (data: NCmsText.IComponentData) => data?.type === 'button'
        },
        <NICmsContentField.IChoiceField>{
            key: 'align',
            type: NICmsContentField.EFieldType.choice,
            title: 'Text Alignment',
            options: HorizontalAlignmentOptions,
            default: 'center'
        },
        <NICmsContentField.IChoiceField>{
            key: 'form_size',
            type: NICmsContentField.EFieldType.choice,
            title: 'Button Size',
            options: FormSizeOptions,
            canShow: (data: NCmsText.IComponentData) => data?.type === 'button'
        },
        // text
        <NICmsContentField.IChoiceField>{
            key: 'text_style',
            type: NICmsContentField.EFieldType.choice,
            title: 'Text Style',
            options: TextStyleOptions,
            default: 'p1',
            canShow: (data: NCmsText.IComponentData) => data?.type !== 'button'
        },
        <NICmsContentField.IChoiceField>{
            key: 'color',
            type: NICmsContentField.EFieldType.choice,
            colorPreview: true,
            title: 'Text Color',
            options: [...UndefinedOption, ...ColorOptions],
            default: '',
            canShow: (data: NCmsText.IComponentData) => data?.type !== 'button' || data?.button_class === 'button-inline'
        },
        <NICmsContentField.IChoiceField>{
            key: 'tag',
            type: NICmsContentField.EFieldType.choice,
            title: 'Force Tag',
            description: 'For SEO reasons you may want to force particular HTML tag',
            options: [...UndefinedOption, ...HtmlTagOptions],
            default: '',
            canShow: (data: NCmsText.IComponentData) => data?.type !== 'button'
        },
        <NICmsContentField.IChoiceField>{
            key: 'section_width',
            type: NICmsContentField.EFieldType.choice,
            title: 'Section Width',
            itemName: 'section_width',
            options: LayoutWidthOptions,
            default: undefined
        },
        <NICmsContentField.IField>{
            key: 'column_align',
            type: NICmsContentField.EFieldType.choice,
            title: 'Column Alignment',
            options: [...UndefinedOption, ...HorizontalAlignmentOptions],
            default: undefined
        },
        <NICmsContentField.IChoiceField>{
            key: 'styles.bg',
            type: NICmsContentField.EFieldType.choice,
            title: 'Background Color',
            options: () => [...UndefinedOption, ...window.app.injector.get<NICmsClientConfig.IConfig>(ICMS_CLIENT_CONFIG).colors],
            colorPreview: true
        }
    ]
})
@Component({
    selector: 'cms-text',
    templateUrl: './cms-text.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [HrefComponent, TagComponent, CommonModule, ICmsClientModule]
})
export class CmsTextComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: NCmsText.IComponentData;
}
